import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { failureReasonsDetailedData, failureReasonData } from 'service/service';
import moment from 'moment';
import ComputeFlatTableBase from './ComputeFlatTableBase';
import { QueryRule, FieldOption } from 'utils/adb-common-util';

const COLUMN_TO_API_MAP: Record<string, string> = {
  'name': 'name',
  'workspace_name': 'workspace_name',
  'failure_reason': 'failure_reason',
  'event_time': 'event_time',
  'job_run_id': 'job_run_id',
  'workspace_id': 'workspace_id'
};

const DEFAULT_COLUMN_NAMES = [
  'Workspace Id', 'Workspace Name', 'Job Run Id', 'Job Name', 'Failure Reason', 'Event Time'
];

const getColumns = () => [
  { key: 'workspace_name', name: 'Workspace Name', minWidth: 250, sortable: true, formatter: ({ row }: any) => <div title={row?.workspace_name}>{row?.workspace_name || '--'}</div> },
  {
    key: 'failure_reason', name: 'Failure Reason', minWidth: 300, sortable: true, formatter: ({ row }: any) => {
      const reason = row?.failure_reason || '--';
      const color = reason === 'Max concurrent runs reached' ? '#ff9800' : '#f44336';
      return <div style={{ color, fontWeight: 500 }} title={reason}>{reason}</div>;
    }
  },
  { key: 'event_time', name: 'Event Time', minWidth: 180, sortable: true, formatter: ({ row }: any) => <div>{row?.event_time ? moment(row.event_time).format('YYYY-MM-DD HH:mm:ss') : '--'}</div> },
  { key: 'name', name: 'Job Name', minWidth: 180, sortable: true, formatter: ({ row }: any) => <div>{row?.name || '--'}</div> },
  { key: 'job_run_id', name: 'Job Run Id', minWidth: 180, sortable: true, formatter: ({ row }: any) => <div>{row?.job_run_id || '--'}</div> },
  { key: 'workspace_id', name: 'Workspace Id', minWidth: 200, sortable: true, formatter: ({ row }: any) => <div title={row?.workspace_id}>{row?.workspace_id || '--'}</div> }
];

const FailureReasonTable = () => {
  const getInitialQueryFilters = useCallback((navState: any) => {
    const filters: QueryRule[] = [];
    const failureReason = navState?.failureReason || navState?.computeType;
    if (failureReason && failureReason !== 'all') {
      return [
        { id: '2', operator: 'AND' as const, field: 'Status', value: failureReason },
        { id: '1', operator: 'AND' as const, field: 'Workspace Name', value: 'all' }
      ];
    }
    return filters;
  }, []);

  const mapQueryRuleToApi = useCallback((rule: QueryRule, workspaceOptions: FieldOption[]) => {
    if (rule.field === "Workspace Name") {
      const match = workspaceOptions.find(opt => opt.label === rule.value);
      if (match && match.value !== 'all') return { field: 'workspace_name', value: match.value };
    } else if (rule.field === "Status") {
      return { field: 'failure_reason', value: rule.value || "Workload failed" };
    }
    return null;
  }, []);

  const transformRecords = (records: any[]) => records.map((record: any) => ({
    name: record.name || '--',
    workspace_name: record.workspace_name || '--',
    failure_reason: record.failure_reason || '--',
    event_time: record.event_time || '',
    job_run_id: record.jobRunId || '--',
    workspace_id: record.workspace_id || '--'
  }));

  const location = useLocation();
  const navFailureReason = location.state?.failureReason || location.state?.computeType;

  const [statusOptions, setStatusOptions] = useState<FieldOption[]>([]);

  useEffect(() => {
    const fetchReasons = async () => {
      try {
        const resp = await failureReasonData('', '', 'both', 'all', 'ALL', 'customDateRange');
        if (resp?.success && Array.isArray(resp.data)) {
          const options = resp.data.map((r: any) => ({
            label: r._id || r.failure_reason,
            value: r._id || r.failure_reason
          }));
          setStatusOptions([{ label: 'All', value: 'all' }, ...options]);
        }
      } catch (e) {
        console.error('Failed to fetch failure reasons:', e);
      }
    };
    fetchReasons();
  }, []);

  const serviceFuncWrapper = useCallback(({
    startDate, endDate, env, sector, location: loc, selectedDateInString,
    page, pageSize, sortField, sortOrder, filters, exportType
  }: any) => failureReasonsDetailedData({
    startDate, endDate, env, sector, location: loc, selectedDateInString,
    page, limit: pageSize, sortField, sortOrder, filters, exportType
  }), []);

  return (
    <ComputeFlatTableBase
      serviceFunc={serviceFuncWrapper}
      pageTitle={navFailureReason || "Failure Reason Details"}
      breadcrumbLabel="Job Analysis"
      breadcrumbPath="/databricksADB/jobsADB"
      rootActiveTab={0}
      middleBreadcrumb={{
        label: 'Failure Reasons',
        path: '/databricksADB/FailureReasons',
        state: { computeType: navFailureReason }
      }}
      tableType="failure-reason-table"
      defaultColumnNames={DEFAULT_COLUMN_NAMES}
      getColumns={getColumns}
      columnToApiMap={COLUMN_TO_API_MAP}
      preserveFilterKey="failure_reason_count_filters"
      statusOptions={statusOptions}
      getInitialQueryFilters={getInitialQueryFilters}
      mapQueryRuleToApi={mapQueryRuleToApi}
      transformRecords={transformRecords}
      emptyStateMessage="No failure reason data found for the selected filters."
    />
  );
};

export default FailureReasonTable;